import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AlarmDropdown from '../board/alarm/AlarmDropdown';
import { useSubscription } from '../../context/SubscriptionContext';
import './CommonHeader.css';

const LAST_POWER_PLANT_ID_KEY = 'lastPowerPlantId';

const extractPowerPlantId = (pathname) => {
  const match = pathname.match(/^\/power-plant\/(\d+)/);
  return match ? match[1] : null;
};

const CommonHeader = ({ user, onLogout, showTabs, activeTab, onTabChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'ADMIN';
  const { remainingDays, requiresPurchase, activeSubscriptionCount, connectedPlantCount } = useSubscription();
  const [showAlarmDropdown, setShowAlarmDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileNavMenu, setShowMobileNavMenu] = useState(false);
  const [lastPowerPlantId, setLastPowerPlantId] = useState(() =>
    sessionStorage.getItem(LAST_POWER_PLANT_ID_KEY)
  );
  const showRestrictedNavigation = Boolean(user) && !isAdmin && requiresPurchase;
  const alarmDropdownRef = useRef(null);
  const alarmButtonRef = useRef(null);
  const userMenuRef = useRef(null);
  const userMenuButtonRef = useRef(null);
  const mobileNavMenuRef = useRef(null);
  const mobileNavButtonRef = useRef(null);
  const isProductsPage = location.pathname === '/products';
  const currentPowerPlantId = extractPowerPlantId(location.pathname);
  const showMobileDetailSelect = Boolean(user) && (showTabs || isProductsPage);

  const handleLogoClick = () => {
    if (isAdmin) {
      navigate('/admin');
    } else if (showRestrictedNavigation) {
      navigate('/products', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    if (currentPowerPlantId) {
      sessionStorage.setItem(LAST_POWER_PLANT_ID_KEY, currentPowerPlantId);
      setLastPowerPlantId(currentPowerPlantId);
    }
  }, [currentPowerPlantId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showAlarmDropdown &&
        alarmDropdownRef.current &&
        !alarmDropdownRef.current.contains(event.target) &&
        alarmButtonRef.current &&
        !alarmButtonRef.current.contains(event.target)
      ) {
        setShowAlarmDropdown(false);
      }

      if (
        showUserMenu &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target) &&
        userMenuButtonRef.current &&
        !userMenuButtonRef.current.contains(event.target)
      ) {
        setShowUserMenu(false);
      }

      if (
        showMobileNavMenu &&
        mobileNavMenuRef.current &&
        !mobileNavMenuRef.current.contains(event.target) &&
        mobileNavButtonRef.current &&
        !mobileNavButtonRef.current.contains(event.target)
      ) {
        setShowMobileNavMenu(false);
      }
    };

    if (showAlarmDropdown || showUserMenu || showMobileNavMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAlarmDropdown, showUserMenu, showMobileNavMenu]);

  useEffect(() => {
    setShowMobileNavMenu(false);
  }, [location.pathname, activeTab]);

  const handleAlarmClick = () => {
    setShowAlarmDropdown(!showAlarmDropdown);
  };

  const handleUserMenuClick = () => {
    setShowUserMenu((prev) => !prev);
  };

  const handleUserMenuNavigate = (path) => {
    setShowUserMenu(false);
    navigate(path);
  };

  const detailTabOptions = [
    { value: 'home', label: 'HOME', description: '발전소 현황 홈' },
    { value: 'trend', label: '트렌드', description: '발전량 추이 확인' },
    { value: 'report', label: '보고서', description: '기간별 리포트' },
    { value: 'error', label: '에러', description: '장애/경고 내역' },
    { value: 'display', label: '전광판', description: '전광판 화면' },
    ...(isAdmin ? [{ value: 'settings', label: '설정', description: '발전소 설정' }] : []),
    { value: 'products', label: '제품 구매', description: '구독/제품 결제' },
  ];

  const mobileNavOptions = showRestrictedNavigation
    ? detailTabOptions.filter((option) => option.value === 'products')
    : detailTabOptions;

  const mobileNavValue = showRestrictedNavigation
    ? 'products'
    : isProductsPage
      ? 'products'
      : activeTab || 'home';

  const selectedMobileNavOption =
    mobileNavOptions.find((option) => option.value === mobileNavValue) || mobileNavOptions[0];

  const navigateToDetailTab = (tab) => {
    const plantId = currentPowerPlantId || lastPowerPlantId;
    if (plantId) {
      navigate(`/power-plant/${plantId}?tab=${tab}`);
      return;
    }
    navigate('/');
  };

  const handleMobileNavSelect = (value) => {
    setShowMobileNavMenu(false);

    if (value === 'products') {
      if (currentPowerPlantId) {
        sessionStorage.setItem(LAST_POWER_PLANT_ID_KEY, currentPowerPlantId);
        setLastPowerPlantId(currentPowerPlantId);
      }
      navigate('/products');
      return;
    }

    if (showTabs && typeof onTabChange === 'function') {
      onTabChange(value);
      return;
    }

    navigateToDetailTab(value);
  };

  const productsLink = (
    <Link
      to="/products"
      className={`header-products-link desktop-only-nav ${location.pathname === '/products' ? 'active' : ''}`}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
        <path d="M3 6h18" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
      <span>제품 구매</span>
    </Link>
  );

  const headerActions = (
    <div className="header-actions">
      {!showRestrictedNavigation && (
        <div className="alarm-icon-wrapper">
          <button
            ref={alarmButtonRef}
            className={`alarm-icon-button ${showAlarmDropdown ? 'active' : ''}`}
            onClick={handleAlarmClick}
            title="알람"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
          </button>
          {showAlarmDropdown && (
            <div ref={alarmDropdownRef} className="alarm-dropdown-container">
              <AlarmDropdown user={user} onClose={() => setShowAlarmDropdown(false)} />
            </div>
          )}
        </div>
      )}
      {productsLink}
    </div>
  );

  return (
    <div className="dashboard-header">
      <div className="header-left">
        <div className="logo" onClick={handleLogoClick}>
          <img src="/gk-logo.png" alt="지케이 로고" className="logo-icon" />
          <span className="logo-text">지케이 모니터링</span>
        </div>
        {showTabs && !showRestrictedNavigation && (
          <nav className="detail-tabs desktop-only-nav">
            <button
              type="button"
              className={`tab-item ${activeTab === 'home' ? 'active' : ''}`}
              onClick={() => onTabChange('home')}
            >
              HOME
            </button>
            <button
              type="button"
              className={`tab-item ${activeTab === 'trend' ? 'active' : ''}`}
              onClick={() => onTabChange('trend')}
            >
              트렌드
            </button>
            <button
              type="button"
              className={`tab-item ${activeTab === 'report' ? 'active' : ''}`}
              onClick={() => onTabChange('report')}
            >
              보고서
            </button>
            <button
              type="button"
              className={`tab-item ${activeTab === 'error' ? 'active' : ''}`}
              onClick={() => onTabChange('error')}
            >
              에러
            </button>
            <button
              type="button"
              className={`tab-item ${activeTab === 'display' ? 'active' : ''}`}
              onClick={() => onTabChange('display')}
            >
              전광판
            </button>
            {isAdmin && (
              <button
                type="button"
                className={`tab-item ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => onTabChange('settings')}
              >
                설정
              </button>
            )}
          </nav>
        )}

        {!showTabs && user && (
          <nav className={`navigation ${isProductsPage ? 'hide-on-mobile-with-select' : ''}`}>
            {!showRestrictedNavigation && (
              <>
                {isAdmin ? (
                  <>
                    <Link
                      to="/admin"
                      className={`nav-item ${location.pathname === '/admin' ? 'active' : ''}`}
                    >
                      전국 발전 현황
                    </Link>
                    <Link
                      to="/admin/create"
                      className={`nav-item ${location.pathname === '/admin/create' ? 'active' : ''}`}
                    >
                      등록
                    </Link>
                    <Link
                      to="/admin/inquiry"
                      className={`nav-item ${location.pathname === '/admin/inquiry' ? 'active' : ''}`}
                    >
                      조회
                    </Link>
                  </>
                ) : (
                  <Link
                    to="/"
                    className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}
                  >
                    내 발전소 현황
                  </Link>
                )}
                <Link
                  to="/notice"
                  className={`nav-item ${location.pathname === '/notice' ? 'active' : ''}`}
                >
                  공지사항
                </Link>
              </>
            )}
            {headerActions}
          </nav>
        )}

        {showMobileDetailSelect && (
          <div className="mobile-nav-select-wrap">
            <button
              ref={mobileNavButtonRef}
              type="button"
              id="mobile-detail-nav"
              className={`mobile-nav-select ${showMobileNavMenu ? 'open' : ''}`}
              onClick={() => setShowMobileNavMenu((prev) => !prev)}
              aria-label="페이지 메뉴"
              aria-haspopup="listbox"
              aria-expanded={showMobileNavMenu}
            >
              <span className="mobile-nav-select-label">
                {selectedMobileNavOption?.label || '메뉴'}
              </span>
              <span className="mobile-nav-select-chevron" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>

            {showMobileNavMenu && (
              <div
                ref={mobileNavMenuRef}
                className="mobile-nav-menu"
                role="listbox"
                aria-labelledby="mobile-detail-nav"
              >
                <div className="mobile-nav-menu-header">메뉴 선택</div>
                <ul className="mobile-nav-menu-list">
                  {mobileNavOptions.map((option) => {
                    const isActive = option.value === mobileNavValue;
                    return (
                      <li key={option.value}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          className={`mobile-nav-menu-item ${isActive ? 'active' : ''}`}
                          onClick={() => handleMobileNavSelect(option.value)}
                        >
                          <span className="mobile-nav-menu-item-text">
                            <span className="mobile-nav-menu-item-label">{option.label}</span>
                            <span className="mobile-nav-menu-item-desc">{option.description}</span>
                          </span>
                          {isActive && (
                            <span className="mobile-nav-menu-check" aria-hidden="true">
                              ✓
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {(showTabs || (isProductsPage && showMobileDetailSelect)) && user && (
          <div className={isProductsPage ? 'mobile-only-header-actions' : undefined}>
            {headerActions}
          </div>
        )}
      </div>

      {user && (
        <div className="header-right">
          <div className="header-user-info">
            {remainingDays !== null && (
              <span className="subscription-remaining" title="구독 만료까지 남은 일수">
                {connectedPlantCount > 1
                  ? `활성 구독 ${activeSubscriptionCount}/${connectedPlantCount} · 최소 D-${remainingDays}일`
                  : `남은 이용 기간 D-${remainingDays}일`}
              </span>
            )}
            <div className="user-menu-wrapper">
              <button
                ref={userMenuButtonRef}
                type="button"
                className={`user-menu-button ${showUserMenu ? 'active' : ''}`}
                onClick={handleUserMenuClick}
                title="내 정보 메뉴"
                aria-label="내 정보 메뉴"
                aria-expanded={showUserMenu}
                aria-haspopup="menu"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 21a8 8 0 1 0-16 0" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
              {showUserMenu && (
                <div ref={userMenuRef} className="user-menu-dropdown" role="menu">
                  <button
                    type="button"
                    className="user-menu-item"
                    role="menuitem"
                    onClick={() => handleUserMenuNavigate('/profile')}
                  >
                    내 정보
                  </button>
                  <button
                    type="button"
                    className="user-menu-item"
                    role="menuitem"
                    onClick={() => handleUserMenuNavigate('/payment-history')}
                  >
                    결제 내역
                  </button>
                </div>
              )}
            </div>
            <div className="header-user-text">
              <span className="user-name">환영합니다, {user.username}님</span>
              <span className="user-role">({user.role})</span>
            </div>
          </div>
          <button onClick={onLogout} className="logout-button">
            로그아웃
          </button>
        </div>
      )}
    </div>
  );
};

export default CommonHeader;
